// Snapshot projectile lists in the order they are shown. Remote players are drawn at the render
// tick (a little behind the newest snapshot) and so are the explosion events; the grenade that
// causes one must leave the screen on the same frame, not an interpolation delay earlier.

import { wholeTicks } from "../net/constants";
import type { ProjectileSnapData } from "../wasm/types";

export interface ProjectileFrame {
  tick: number;
  list: ProjectileSnapData[];
}

/** Snapshots held at most (one per tick, 1 s); a stalled render clock cannot grow the queue. */
const MAX_FRAMES = wholeTicks(1);

export class ProjectileTimeline {
  private readonly frames: ProjectileFrame[] = [];

  /** Adds the list of the snapshot of `tick`. Snapshots that arrive out of order are dropped: they are stale. */
  push(tick: number, list: ProjectileSnapData[]): void {
    const frames = this.frames;
    const last = frames[frames.length - 1];
    if (last !== undefined && tick <= last.tick) return;
    if (frames.length >= MAX_FRAMES) frames.shift();
    frames.push({ tick, list });
  }

  /** The newest frame at or before `renderTick` that was not returned before; null when nothing changed. */
  take(renderTick: number): ProjectileFrame | null {
    const frames = this.frames;
    let n = 0;
    while (n < frames.length && (frames[n] as ProjectileFrame).tick <= renderTick) n++;
    if (n === 0) return null;
    const shown = frames[n - 1] as ProjectileFrame;
    frames.splice(0, n);
    return shown;
  }

  clear(): void {
    this.frames.length = 0;
  }
}
